import { Link } from "react-router-dom";
import { fileUrl, formatPrice } from "../lib/api";
import { useCart } from "../context/CartContext";
import { Button } from "./ui/button";
import { Card } from "./ui/card";
import { ShoppingCart, ImageOff, Star } from "lucide-react";

export function ProductCard({ product }) {
  const { addItem } = useCart();
  const img = fileUrl(product.image_url);
  const soldOut = !product.available || (product.stock != null && product.stock <= 0);
  return (
    <Card className="group overflow-hidden rounded-2xl border-slate-200 hover:shadow-xl hover:shadow-emerald-900/5 transition-all duration-300 flex flex-col" data-testid={`product-card-${product.id}`}>
      <Link to={`/prodotto/${product.slug || product.id}`} className="block relative">
        <div className="aspect-square bg-emerald-50/60 overflow-hidden flex items-center justify-center">
          {img ? <img src={img} alt={product.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" /> : <ImageOff className="w-12 h-12 text-emerald-200" />}
        </div>
        {product.old_price && product.old_price > product.price && !soldOut && (
          <span className="absolute top-3 left-3 bg-accent text-white text-xs font-bold px-2.5 py-1 rounded-full">OFFERTA</span>
        )}
        {soldOut && <span className="absolute top-3 left-3 bg-slate-800 text-white text-xs font-bold px-2.5 py-1 rounded-full">NON DISPONIBILE</span>}
        {product.featured && !soldOut && (
          <span className="absolute top-3 right-3 inline-flex items-center gap-1 bg-amber-400 text-amber-950 text-xs font-bold px-2.5 py-1 rounded-full shadow-sm" data-testid="featured-badge"><Star className="w-3 h-3 fill-current" /> In evidenza</span>
        )}
      </Link>
      <div className="p-4 flex flex-col flex-1">
        {product.category_name && <span className="text-[11px] font-semibold uppercase tracking-wider text-emerald-600">{product.category_name}</span>}
        <Link to={`/prodotto/${product.slug || product.id}`}><h3 className="mt-1 font-display font-bold text-slate-900 leading-tight line-clamp-2 hover:text-primary">{product.name}</h3></Link>
        <p className="mt-1 text-sm text-slate-500 line-clamp-2 flex-1">{product.short_description}</p>
        <div className="mt-3 flex items-center justify-between">
          <div className="flex items-baseline gap-2">
            <span className="text-lg font-extrabold text-slate-900">{formatPrice(product.price)}</span>
            {product.old_price && product.old_price > product.price && <span className="text-sm text-slate-400 line-through">{formatPrice(product.old_price)}</span>}
          </div>
          <Button size="sm" disabled={soldOut} onClick={() => addItem(product)} aria-label={`Aggiungi ${product.name} al carrello`} data-testid={`add-to-cart-${product.id}`}><ShoppingCart className="w-4 h-4" /></Button>
        </div>
      </div>
    </Card>
  );
}

export function SectionTitle({ eyebrow, title, subtitle, center }) {
  return (
    <div className={center ? "text-center max-w-2xl mx-auto" : "max-w-2xl"}>
      {eyebrow && <span className="text-xs font-semibold tracking-widest uppercase text-emerald-600">{eyebrow}</span>}
      <h2 className="mt-2 text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-slate-900 font-display text-balance">{title}</h2>
      {subtitle && <p className="mt-3 text-base sm:text-lg text-slate-500 leading-relaxed">{subtitle}</p>}
    </div>
  );
}

export function StatCard({ label, value, icon: Icon, accent = "emerald" }) {
  const colors = { emerald: "bg-emerald-50 text-emerald-600", amber: "bg-amber-50 text-amber-600", sky: "bg-sky-50 text-sky-600", violet: "bg-violet-50 text-violet-600", rose: "bg-rose-50 text-rose-600" };
  return (
    <Card className="p-5 rounded-2xl border-slate-200">
      <div className="flex items-center justify-between">
        <span className="text-sm text-slate-500">{label}</span>
        {Icon && <div className={`p-2 rounded-xl ${colors[accent]}`}><Icon className="w-5 h-5" /></div>}
      </div>
      <div className="mt-2 text-3xl font-extrabold text-slate-900 font-display">{value}</div>
    </Card>
  );
}
